<template>
  <div :class="containerClass">
    <h2>{{ isEditMode ? 'Edit' : 'Add to' }} Skills Library</h2>

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Title" label-for="title" label-class="form-label">
        <b-form-input
          id="title"
          placeholder="Enter title"
          v-model.trim="libraryItem.title"
          maxlength="250"
          :state="!$v.libraryItem.title.required && $v.libraryItem.title.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Description (Optional)" label-for="description" label-class="form-label">
        <editor
          id="description"
          name="description"
          :init="{
            height: 400,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
             alignleft aligncenter alignright alignjustify | \
             bullist numlist outdent indent | removeformat | help',
          }"
          v-model="libraryItem.description"
          placeholder="Enter description (optional)"
        />
      </b-form-group>

      <b-form-group label="Category (Optional)" label-for="category" label-class="form-label">
        <b-form-select
          id="category"
          v-model="libraryItem.category"
          :options="categoryOptions"
          placeholder="Select category"
        />
      </b-form-group>

      <b-form-group label="Source (Optional)" label-for="source" label-class="form-label">
        <b-form-select
          id="source"
          v-model="libraryItem.source"
          :options="sourceOptions"
          placeholder="Select source"
        />
      </b-form-group>

      <b-form-group label="Related Program" label-for="program" label-class="form-label">
        <v-select
          id="program"
          class="form-control v-select-custom"
          label="title"
          v-model="libraryItem.program"
          :reduce="program => program.id"
          placeholder="Select a program"
          :options="allPrograms"
          :loading="areProgramsLoading"
          :class="!$v.libraryItem.program.required && $v.libraryItem.program.$dirty ? 'form-control is-invalid' : ''"
        >
          <template slot="option" slot-scope="option">
            <span>{{ option.title }}</span>
          </template>
          <template slot="no-options">
            <span v-if="areProgramsLoading">Loading programs...</span>
            <span v-else>No programs available</span>
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.libraryItem.program.required && $v.libraryItem.program.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label-for="course" label-class="form-label">
        <template #label>
          Related Courses (Optional)
          <md-icon
            id="course-info-icon"
            class="text-muted ml-1"
            style="font-size: 18px; cursor: pointer; vertical-align: middle"
          >
            info
          </md-icon>
          <b-tooltip target="course-info-icon" triggers="hover">
            When associated with courses, the video will be displayed in the LMS within the selected courses.
          </b-tooltip>
        </template>
        <v-select
          id="course"
          class="form-control v-select-custom"
          label="title"
          v-model="libraryItem.courses"
          :reduce="course => course.id"
          placeholder="Select courses (optional)"
          :options="allCourses"
          :loading="areCoursesLoading"
          :disabled="!libraryItem.program"
          clearable
          multiple
        >
          <template slot="option" slot-scope="option">
            <span>{{ option.title }}</span>
          </template>
          <template slot="no-options">
            <span v-if="areCoursesLoading">Loading courses...</span>
            <span v-else-if="!libraryItem.program">Please select a program first</span>
            <span v-else>No courses available for this program</span>
          </template>
        </v-select>
        <small class="form-text text-muted" v-if="!libraryItem.program">
          Select a program to view associated courses.
        </small>
        <small class="form-text text-muted" v-else-if="allCourses.length === 0 && !areCoursesLoading">
          No courses found for the selected program.
        </small>
      </b-form-group>

      <b-form-group label="Video Link" label-for="video" label-class="form-label" class="row-align-items-center">
        <b-form-input
          id="video"
          placeholder="Paste video URL (e.g., https://example.com/video.mp4)"
          v-model.trim="libraryItem.video_url"
          :state="!$v.libraryItem.video_url.required && $v.libraryItem.video_url.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
        
        <div v-if="libraryItem.video_url && !isEditMode" class="mt-3">
          <video id="video" width="100%" height="200" controls crossOrigin="anonymous">
            <source :src="libraryItem.video_url" type="video/mp4" />
          </video>
        </div>
      </b-form-group>

      <b-form-group label="Thumbnail (Optional)" label-for="thumbnail" label-class="form-label">
        <template v-if="!libraryItem.thumbnail_url">
          <b-form-file
            id="thumbnail"
            placeholder="Select thumbnail image (optional)"
            :browse-text="$t('generalMsgs.browse')"
            v-model="uploadedThumbFile"
            @input="uploadThumbnail"
            :disabled="isThumbUploading"
            accept="image/png, image/jpeg, image/jpg"
          />
        </template>

        <div v-else>
          <div class="d-flex justify-content-end mb-3">
            <b-btn style="width: 80px" variant="danger" @click.prevent="removeThumbnail"> Remove </b-btn>
          </div>
          <img :src="libraryItem.thumbnail_url" style="max-width: 100%; height: auto; max-height: 200px" />
        </div>
      </b-form-group>

      <div class="d-flex gap-2">
        <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
          <i v-if="isSaving" class="fas fa-circle-notch fa-spin"></i>
          <span v-else>Save</span>
        </b-btn>
        <b-btn variant="secondary" :to="{ name: 'skills-library' }" style="width: 150px" class="btn-normal">
          Cancel
        </b-btn>
      </div>
    </b-form>
  </div>
</template>

<script>
import Page from '@/components/Page.vue';
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';
import Editor from '@tinymce/tinymce-vue';

export default {
  components: {
    Editor,
  },
  extends: Page,

  data() {
    return {
      title: 'Configure Skills Library',

      libraryItem: {
        title: '',
        description: '',
        video_url: null,
        thumbnail_url: null,
        program: null,
        courses: [],
        assigned_school: [],
        is_superuser_skill: false,
        category: '',
        source: '',
      },
      uploadedThumbFile: null,
      allPrograms: [],
      allCourses: [],

      categoryOptions: [
        { value: '', text: 'Select category' },
        { value: 'hygiene', text: 'Hygiene' },
        { value: 'mobility', text: 'Mobility' },
        { value: 'vital_signs', text: 'Vital Signs' },
        { value: 'personal_care', text: 'Personal Care' },
        { value: 'elimination', text: 'Elimination' },
        { value: 'positioning', text: 'Positioning' },
        { value: 'nutrition', text: 'Nutrition' },
        { value: 'infection_control', text: 'Infection Control' },
        { value: 'range_of_motion', text: 'Range of Motion' },
        { value: 'bed_making', text: 'Bed Making' },
        { value: 'safety', text: 'Safety' },
        { value: 'emergency', text: 'Emergency' },
      ],
      sourceOptions: [
        { value: '', text: 'Select source' },
        { value: 'Transition', text: 'Transition' },
        { value: 'Skillform', text: 'Skillform' },
        { value: 'Hartman', text: 'Hartman' },
        { value: '4YourCNA', text: '4YourCNA' },
        { value: 'NURSE JAR', text: 'NURSE JAR' },
        { value: 'Nurse Eunice', text: 'Nurse Eunice' },
        { value: 'CNASkill', text: 'CNASkill' },
        { value: 'XY Nursing Skills', text: 'XY Nursing Skills' },
        { value: 'All Healthcare Careers', text: 'All Healthcare Careers' },
      ],

      isLoading: false,
      isSaving: false,
      areProgramsLoading: false,
      areCoursesLoading: false,
      isThumbUploading: false,
      isInitialLoad: true,
    };
  },

  validations() {
    return {
      libraryItem: {
        title: { required },
        description: {},
        video_url: { required },
        program: { required },
        thumbnail_url: {},
        courses: {},
        category: {},
        source: {},
      },
    };
  },

  computed: {
    isFormLoading() {
      return this.isLoading || this.areProgramsLoading || this.areCoursesLoading || this.isSaving;
    },
    isEditMode() {
      return !!this.$route.params.id;
    },
  },

  watch: {
    'libraryItem.program': {
      handler(newProgramId, oldProgramId) {
        // Don't clear course during initial load in edit mode
        if (!this.isInitialLoad && oldProgramId !== undefined) {
          this.libraryItem.courses = [];
        }
        // Fetch courses for the new program
        if (newProgramId) {
          this.fetchProgramCourses(newProgramId);
        } else {
          this.allCourses = [];
        }
      },
    },
  },

  methods: {
    ...mapActions('skills', [
      'createSkillLibrary',
      'updateSkillLibrary',
      'getSkillLibraryById',
      'getSkillLibraryUploadUrl',
    ]),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('courses', ['getLmsCourses', 'getProgramAssignedCourses']),

    async onSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.isSaving = true;

        try {
          // Ensure is_superuser_skill is false for school-created skills
          const data = {
            ...this.libraryItem,
            is_superuser_skill: false,
          };

          if (this.isEditMode) {
            await this.updateSkillLibrary({ id: this.$route.params.id, data });
          } else {
            await this.createSkillLibrary(data);
          }
          this.$router.push({ name: 'skills-library' });
          this.makeToast({ variant: 'success', msg: 'Successfully saved!' });
        } catch (error) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }

        this.isSaving = false;
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all required fields.' });
      }
    },


    async uploadThumbnail() {
      this.isThumbUploading = true;

      // check file size and type
      if (this.uploadedThumbFile.size > 5000000) {
        this.makeToast({ variant: 'danger', msg: 'File size should not exceed 5MB' });
        this.uploadedThumbFile = null;
        this.isThumbUploading = false;
        return;
      }
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(this.uploadedThumbFile.type)) {
        this.makeToast({ variant: 'danger', msg: 'File type should be png, jpg, or jpeg' });
        this.uploadedThumbFile = null;
        this.isThumbUploading = false;
        return;
      }

      try {
        const urlResp = await this.getSkillLibraryUploadUrl({
          file_name: this.uploadedThumbFile.name,
          content_type: this.uploadedThumbFile.type,
          upload_type: 'thumbnails',
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: this.uploadedThumbFile,
        });
        this.libraryItem.thumbnail_url = urlResp.data.upload_url.split('?')[0];
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.uploadedThumbFile = null;
      }
      this.isThumbUploading = false;
    },


    removeThumbnail() {
      this.libraryItem.thumbnail_url = null;
      this.uploadedThumbFile = null;
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;

      try {
        const response = await this.getAllPrograms({ ordering: 'id' });
        // Handle different response structures
        if (response && response.data && Array.isArray(response.data.results)) {
          this.allPrograms = response.data.results;
        } else if (response && Array.isArray(response.data)) {
          this.allPrograms = response.data;
        } else if (Array.isArray(response)) {
          this.allPrograms = response;
        } else {
          this.allPrograms = [];
        }
        
      } catch (error) {
        this.allPrograms = [];
        this.makeToast({ variant: 'danger', msg: 'Failed to load programs' });
      }
      
      this.areProgramsLoading = false;
    },

    async fetchProgramCourses(programId) {
      if (!programId) {
        this.allCourses = [];
        return;
      }

      this.areCoursesLoading = true;

      try {
        const response = await this.getProgramAssignedCourses({ program: programId });
        // Handle different response structures
        if (response && response.data && Array.isArray(response.data.results)) {
          this.allCourses = response.data.results;
        } else if (response && Array.isArray(response.data)) {
          this.allCourses = response.data;
        } else if (Array.isArray(response)) {
          this.allCourses = response;
        } else {
          this.allCourses = [];
        }
        
      } catch (error) {
        this.allCourses = [];
        this.makeToast({ variant: 'danger', msg: 'Failed to load courses for this program' });
      }
      
      this.areCoursesLoading = false;
    },

    async fetchLibraryItem() {
      this.isLoading = true;

      const response = await this.getSkillLibraryById(this.$route.params.id);
      this.libraryItem = {
        ...response.data,
        courses: response.data.courses || [],
        assigned_school: response.data.assigned_school || [],
        category: response.data.category || '',
        source: response.data.source || '',
      };

      this.isLoading = false;
    },
  },

  async mounted() {
    await this.fetchPrograms();
    if (this.isEditMode) {
      await this.fetchLibraryItem();
      // Fetch courses for the selected program if exists
      if (this.libraryItem.program) {
        await this.fetchProgramCourses(this.libraryItem.program);
      }
    }
    // Mark initial load as complete
    this.$nextTick(() => {
      this.isInitialLoad = false;
    });
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
</style>
